import { Controller } from "@hotwired/stimulus";
import { checkCondition, evaluateCondition, extractCondition, fromBlocks, OPS } from "../lib/condition";
import { bindingScope } from "../lib/variable_chain";

/**
 * La sous-page Condition de la toolbox (plan media-template-generators §4) : elle édite le
 * CODE de la condition de visibilité du calque sélectionné (`bindings.visible.expr`).
 *
 * Le code est la condition. Les cartes en sont la lecture, RENDUES PAR LE SERVEUR depuis le
 * document (ConditionExpression::Cards) ; ici on les manipule en place — basculer ET / OU,
 * changer un opérateur, ajouter une règle (par les gabarits, les mêmes partials) — puis on
 * relit le code dans le DOM (lib condition.ts : `fromBlocks`) et on le pose sur le canvas.
 * Après la sauvegarde, la sous-page se recharge en morph : le serveur rend des cartes
 * identiques à celles qu'on vient de régler. Un code collé qui se lit part de même ; ses
 * cartes arrivent avec ce rechargement.
 *
 * Il parle au canvas par événements window (le panneau vit dans un turbo-frame) :
 * `text:bind` pose la condition (champ `visible`, scope déduit de ses tokens : subject s'ils
 * sont du contrat du sujet, company sinon), `text:unbind` la retire. Le canvas, lui, évalue la condition avec
 * SES valeurs et pose le fantôme (lib fabric_bindings) ; le point d'une règle l'évalue ici,
 * avec les valeurs d'aperçu.
 *
 * Embarqué (`embedded`) dans un autre panneau — un cas d'une couleur selon l'annonce —, il
 * ne parle pas au canvas : le code remonte au parent par `condition-editor:change`.
 *
 * Il ne touche à aucune classe : les partials portent les leurs, le JS pose des attributs
 * (hidden, aria-checked, aria-expanded, aria-invalid, data-join, data-pass).
 *
 * Naming: "condition-editor" in HTML.
 */
export default class extends Controller {
  static targets = [
    "cards", "codeToggle", "codeBox", "code", "error", "copy",
    "cardTemplate", "ruleTemplate", "joinTemplate", "alsoTemplate",
  ];

  static values = {
    samples: Object,
    labels: Object,
    subjectTokens: Array,
    embedded: Boolean,
    // Le délai laisse finir un réglage avant de le poser sur le canvas (autosave derrière).
    debounce: { type: Number, default: 400 },
  };

  connect() {
    this.code = this.hasCodeTarget ? this.codeTarget.value : "";
    // Un cas neuf (variant « case ») montre une carte d'emblée : sa condition existe sans
    // geste, le parent la reçoit tout de suite
    if (this.embeddedValue && !this.code && this.hasCardsTarget && this.cardsTarget.querySelector("[data-rule]")) this.sync();
  }

  disconnect() {
    clearTimeout(this._timer);
    clearTimeout(this._copied);
  }

  // Rechargé en morph après une sauvegarde : le document fait foi.
  morphed(event) {
    if (event.target === this.element && this.hasCodeTarget) this.code = this.codeTarget.value;
  }

  // Les valeurs d'aperçu changent (menu « Variables du document ») : les points suivent.
  refreshSamples(event) {
    this.samplesValue = event.detail?.values || {};
    if (this.hasCardsTarget) this.cardsTarget.querySelectorAll("[data-rule]").forEach((row) => this.mark(row));
  }

  // Le pli Code garde son état à travers les rechargements.
  keepFold(event) {
    if (["hidden", "aria-expanded"].includes(event.detail.attributeName)) event.preventDefault();
  }

  // ==================== Les gestes sur les cartes ====================

  setAction(event) {
    const chosen = event.currentTarget.dataset.cardAction;
    this.card(event).querySelectorAll("[data-card-action]").forEach((radio) => {
      radio.setAttribute("aria-checked", String(radio.dataset.cardAction === chosen));
    });
    this.sync();
  }

  toggleJoin(event) {
    const card = this.card(event);
    const next = event.currentTarget.dataset.join === "and" ? "or" : "and";
    this.setJoin(card, next);
    this.sync();
  }

  addRule(event) {
    const card = this.card(event);
    const rules = card.querySelector("[data-rules]");
    const row = this.clone(this.ruleTemplateTarget);
    const last = card.querySelector("[data-rule]:last-of-type");
    if (last) {
      this.pickKey(row, last.querySelector("[data-field='key']").value);
      rules.append(this.clone(this.joinTemplateTarget));
    }
    rules.append(row);
    this.refreshCard(card);
    this.sync();
  }

  removeRule(event) {
    const row = this.rule(event);
    const card = this.card(event);
    // Le lien d'avant part avec la règle ; la première règle emporte le lien d'après
    const link = row.previousElementSibling?.matches("[data-join-row]") ? row.previousElementSibling
      : row.nextElementSibling?.matches("[data-join-row]") ? row.nextElementSibling : null;
    link?.remove();
    row.remove();
    if (card.querySelector("[data-rule]")) this.refreshCard(card);
    else this.dropCard(card);
    this.sync();
  }

  addBlock() {
    const card = this.clone(this.cardTemplateTarget);
    const last = this.cardsTarget.querySelector("[data-card]:last-of-type");
    if (last) {
      this.pickKey(card.querySelector("[data-rule]"), last.querySelector("[data-field='key']").value);
      this.cardsTarget.append(this.clone(this.alsoTemplateTarget));
    }
    this.cardsTarget.append(card);
    this.sync();
  }

  removeBlock(event) {
    this.dropCard(this.card(event));
    this.sync();
  }

  changeRule(event) {
    const field = event.currentTarget;
    if (field.dataset.field === "op") {
      const value = this.rule(event).querySelector("[data-field='value']");
      value.hidden = !OPS.find((op) => op.op === field.value).needsValue;
    }
    this.sync();
  }

  // ==================== Le DOM, lu comme des cartes ====================

  /** Les cartes telles que le panneau les montre — la vérité du code. */
  readBlocks() {
    return Array.from(this.cardsTarget.querySelectorAll("[data-card]")).map((card) => ({
      action: card.querySelector("[data-card-action][aria-checked='true']")?.dataset.cardAction || "show",
      join: card.querySelector("[data-join]")?.dataset.join || "and",
      rules: Array.from(card.querySelectorAll("[data-rule]")).map((row) => this.readRule(row)),
    }));
  }

  readRule(row) {
    const field = (name) => row.querySelector(`[data-field='${name}']`).value;
    const op = field("op");
    const rule = { key: field("key"), op };
    if (OPS.find((o) => o.op === op).needsValue) rule.value = field("value");
    return rule;
  }

  /** Le code suit les cartes : relu, affiché, chaque point rejoué, posé sur le canvas. */
  sync() {
    this.code = fromBlocks(this.readBlocks());
    this.codeTarget.value = this.code;
    this.copyTarget.disabled = !this.code;
    this.cardsTarget.querySelectorAll("[data-rule]").forEach((row) => this.mark(row));
    this.schedule();
  }

  // Le point : la règle passe-t-elle pour les valeurs d'aperçu ?
  mark(row) {
    const dot = row.querySelector("[data-dot]");
    const passes = evaluateCondition(fromBlocks([{ join: "and", rules: [this.readRule(row)], action: "show" }]), this.samplesValue);
    dot.toggleAttribute("data-pass", passes);
    dot.title = this.labelsValue[passes ? "passes" : "fails"];
  }

  // Une carte réglée : la croix des règles dès qu'il y en a deux, le lien et sa phrase
  // sous le premier seulement
  refreshCard(card) {
    const rules = card.querySelectorAll("[data-rule]");
    rules.forEach((row) => { row.querySelector("[data-rule-remove]").hidden = rules.length < 2; });
    this.setJoin(card, card.querySelector("[data-join]")?.dataset.join || "and");
  }

  setJoin(card, join) {
    card.querySelectorAll("[data-join]").forEach((button) => {
      button.dataset.join = join;
      button.textContent = this.labelsValue[join];
    });
    card.querySelectorAll("[data-join-hint]").forEach((hint, index) => {
      hint.textContent = index === 0 ? this.labelsValue[join === "and" ? "all_must_pass" : "one_suffices"] : "";
    });
  }

  // Une carte qui part emporte son séparateur, et le premier séparateur ne précède rien
  dropCard(card) {
    card.remove();
    this.cardsTarget.querySelectorAll("[data-also]").forEach((also) => also.remove());
    this.cardsTarget.querySelectorAll("[data-card]").forEach((kept, index) => {
      if (index > 0) kept.before(this.clone(this.alsoTemplateTarget));
    });
  }

  // Une règle neuve ne reprend pas la variable d'à côté, si le contrat en a une autre
  pickKey(row, avoid) {
    const select = row.querySelector("[data-field='key']");
    const other = Array.from(select.options).find((option) => option.value !== avoid);
    if (other) select.value = other.value;
  }

  clone(template) {
    return template.content.firstElementChild.cloneNode(true);
  }

  card(event) {
    return event.currentTarget.closest("[data-card]");
  }

  rule(event) {
    return event.currentTarget.closest("[data-rule]");
  }

  // ==================== Le code ====================

  toggleCode() {
    const open = this.codeBoxTarget.hidden;
    this.codeBoxTarget.hidden = !open;
    this.codeToggleTarget.setAttribute("aria-expanded", String(open));
  }

  // Dès que le code se lit, il remplace la condition ; tant qu'il ne se lit pas, l'erreur
  // s'affiche et la condition d'avant reste en place.
  editCode(event) {
    const draft = extractCondition(event.currentTarget.value);
    const error = draft ? checkCondition(draft) : null;
    this.errorTarget.textContent = error || "";
    this.errorTarget.hidden = !error;
    this.codeTarget.toggleAttribute("aria-invalid", Boolean(error));
    if (error) return;

    this.code = draft;
    this.copyTarget.disabled = !this.code;
    this.schedule();
  }

  leaveCode() {
    this.errorTarget.hidden = true;
    this.codeTarget.removeAttribute("aria-invalid");
    this.codeTarget.value = this.code;
  }

  copy() {
    navigator.clipboard?.writeText(this.code).catch(() => {});
    const button = this.copyTarget;
    const show = (done) => {
      button.querySelector("[data-copy-idle]").hidden = done;
      button.querySelector("[data-copy-done]").hidden = !done;
      button.title = this.labelsValue[done ? "copied" : "copy"];
    };
    show(true);
    clearTimeout(this._copied);
    this._copied = setTimeout(() => show(false), 1200);
  }

  // ==================== Vers le canvas ====================

  schedule() {
    clearTimeout(this._timer);
    this._timer = setTimeout(() => this.apply(), this.debounceValue);
  }

  apply() {
    clearTimeout(this._timer);
    this._timer = null;
    const code = this.code.trim();
    if (this.embeddedValue) return this.dispatch("change", { detail: { code } });

    const detail = code ? { field: "visible", expr: code, scope: bindingScope(code, this.subjectTokensValue) } : { field: "visible" };
    window.dispatchEvent(new CustomEvent(code ? "text:bind" : "text:unbind", { detail }));
  }
}
